import React from "react";
import { NavLink, Outlet, useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  Radar, Trees, ListChecks, SlidersHorizontal, LogOut, Shield, HelpCircle,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { BrandIcon } from "@/components/BrandIcon";
import { TOUR_START_EVENT } from "@/lib/tour";

const navItems = [
  { to: "/radar/tierra", label: "Tierra", icon: Trees, testid: "nav-radar-tierra" },
  { to: "/radar", label: "Inmuebles", icon: Radar, testid: "nav-radar-mapa" },
  { to: "/radar/tablero", label: "Listados", icon: ListChecks, testid: "nav-radar-tablero" },
  { to: "/radar/ajustes", label: "Ajustes", icon: SlidersHorizontal, testid: "nav-radar-ajustes" },
];

// Items visibles solo a platform_admin (nivel plataforma, cross-cuenta).
const platformItems = [
  { to: "/radar/plataforma", label: "Plataforma", icon: Shield, testid: "nav-radar-plataforma" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  // "Hacé el tour" — dispara el onboarding guiado. Si el usuario no está en
  // Tierra, navega primero y espera a que la vista monte antes de dispararlo.
  const handleStartTour = () => {
    const fire = () => window.dispatchEvent(new CustomEvent(TOUR_START_EVENT));
    if (location.pathname !== "/radar/tierra") {
      navigate("/radar/tierra");
      // El listener vive en RadarTierra; le damos tiempo al mount + primer paint.
      setTimeout(fire, 700);
    } else {
      fire();
    }
  };

  return (
    <div className="min-h-screen flex bg-slate-50 font-body">
      <aside className="w-64 bg-slate-900 text-slate-200 flex flex-col fixed inset-y-0 left-0" data-testid="sidebar">
        <div className="px-6 py-6 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <BrandIcon size={36} className="shrink-0" data-testid="brand-icon-sidebar" />
            <div>
              <p className="font-heading text-base font-bold text-white leading-none" data-testid="brand-wordmark-sidebar">LandHo<span className="text-emerald-400">!</span></p>
              <p className="text-[10px] font-medium text-slate-400 leading-tight mt-0.5">Radar de oportunidades</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 py-6 px-3 space-y-1">
          {navItems.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/radar"}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                  isActive
                    ? "bg-white/5 text-white border-l-2 border-emerald-500"
                    : "text-slate-400 hover:text-white hover:bg-white/5 border-l-2 border-transparent"
                }`
              }
            >
              <Icon className="w-4 h-4 text-emerald-400" />
              <span className="font-medium">{label}</span>
            </NavLink>
          ))}

          {/* Nav de plataforma — solo visible a platform_admin (Fase invite-only).
              Guard client-side + server-side (los endpoints exigen require_platform_admin). */}
          {user?.role === "platform_admin" && (
            <>
              <div className="pt-4 mt-4 border-t border-slate-800/60">
                <p className="px-4 pb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                  Plataforma
                </p>
              </div>
              {platformItems.map(({ to, label, icon: Icon, testid }) => (
                <NavLink
                  key={to}
                  to={to}
                  data-testid={testid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                      isActive
                        ? "bg-white/5 text-white border-l-2 border-amber-500"
                        : "text-slate-400 hover:text-white hover:bg-white/5 border-l-2 border-transparent"
                    }`
                  }
                >
                  <Icon className="w-4 h-4 text-amber-400" />
                  <span className="font-medium">{label}</span>
                </NavLink>
              ))}
            </>
          )}

          {/* "Hacé el tour" — SIEMPRE al final del menú (debajo de todos los
              ítems, incluyendo Plataforma si aplica). Re-lanza el onboarding
              guiado a demanda; útil para demos y para gente nueva. */}
          <div className="pt-4 mt-4 border-t border-slate-800/60">
            <button
              type="button"
              data-testid="nav-start-tour"
              onClick={handleStartTour}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-400 hover:text-white hover:bg-white/5 border-l-2 border-transparent transition-colors"
            >
              <HelpCircle className="w-4 h-4 text-emerald-400" />
              <span className="font-medium">Hacé el tour</span>
            </button>
          </div>
        </nav>

        <div className="px-4 py-4 border-t border-slate-800">
          <div className="flex items-center gap-3 mb-3" data-testid="user-info">
            <Avatar className="w-9 h-9 rounded-none">
              <AvatarImage src={user?.picture} alt={user?.name} />
              <AvatarFallback className="rounded-none bg-slate-700 text-white text-xs">
                {user?.name?.slice(0, 2).toUpperCase() || "U"}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate" data-testid="user-name">{user?.name}</p>
              <p className="text-[11px] uppercase tracking-wider text-slate-400">
                {user?.role === "platform_admin"
                  ? "Plataforma"
                  : user?.role === "admin" ? "Administrador" : "Usuario"}
              </p>
            </div>
          </div>
          <Button
            data-testid="logout-btn"
            onClick={handleLogout}
            variant="ghost"
            className="w-full justify-start text-slate-400 hover:text-white hover:bg-white/5 rounded-none px-4 py-2 h-auto text-sm"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Cerrar sesión
          </Button>
        </div>
      </aside>

      <main className="flex-1 ml-64 min-h-screen flex flex-col">
        <div className="flex-1">
          <Outlet />
        </div>
        {/* Footer discreto — atribución CC BY-SA 4.0 + T&C.
            Bajo, texto chico, no invade la vista de trabajo. */}
        <footer className="border-t border-slate-200 bg-white px-6 py-3 text-[11px] text-slate-500 flex items-center justify-between"
                data-testid="app-footer">
          <span>LandHo!</span>
          <Link
            to="/fuentes"
            data-testid="footer-fuentes-link"
            className="hover:text-emerald-700 hover:underline"
          >
            Fuentes y créditos
          </Link>
        </footer>
      </main>
    </div>
  );
}
